import dayjs, { Dayjs } from 'dayjs'
import { Box, Typography } from '@mui/material'
import { Services } from '../../../../../store/slices/timesheet/timesheetPageSlice'
import CustomDatePicker from '../../../../general/CustomDatePicker/CustomDatePicker'
import CustomTimePicker from '../../../../general/CustomTimePicker/CustomTimePicker'
import { LightTooltip } from '../../../../LighTooltip'

interface ServiceExecutionDateTimeProps {
	isReserveService: boolean
	isSecondaryDriverService: boolean
	startTime: string
	startDate: string
	endTime: string
	endDate: string
	startTimeMatches: boolean
	showEndTimeTooltip: boolean
	startTimeTooltipOpen: boolean
	endTimeTooltipOpen: boolean
	serviceDetail: Services
	setStartTimeTooltipOpen: (open: boolean) => void
	setEndTimeTooltipOpen: (open: boolean) => void
	onStartTimeChange: (newValue: Dayjs | null) => void
	onStartDateChange: (newValue: string) => void
	onEndTimeChange: (newValue: Dayjs | null) => void
	onEndDateChange: (newValue: string) => void
}

export default function ServiceExecutionDateTime({
	isReserveService,
	isSecondaryDriverService,
	startTime,
	startDate,
	endTime,
	endDate,
	startTimeMatches,
	showEndTimeTooltip,
	startTimeTooltipOpen,
	endTimeTooltipOpen,
	serviceDetail,
	setStartTimeTooltipOpen,
	setEndTimeTooltipOpen,
	onStartTimeChange,
	onStartDateChange,
	onEndTimeChange,
	onEndDateChange,
}: Readonly<ServiceExecutionDateTimeProps>) {
	if (isReserveService) {
		return (
			<Box
				sx={{
					width: '10.25rem',
					margin: 0,
					padding: '0.5rem 1rem 0.5rem 1.25rem',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: 0.5,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Typography
						variant='body2'
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 400,
							fontSize: '0.75rem',
							lineHeight: '130%',
							letterSpacing: '0.17px',
							color: 'var(--primary-dark)',
						}}
					>
						{dayjs(serviceDetail.serviceExecution?.startDateTime ?? serviceDetail.service?.startDateTime).format(
							'HH:mm',
						)}
					</Typography>
					<Typography
						variant='body2'
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 400,
							fontSize: '0.75rem',
							lineHeight: '130%',
							letterSpacing: '0.17px',
							color: 'var(--primary-dark)',
						}}
					>
						{dayjs(serviceDetail.serviceExecution?.startDateTime ?? serviceDetail.service?.startDateTime).format(
							'DD/MM/YYYY',
						)}
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Typography
						variant='body2'
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 400,
							fontSize: '0.75rem',
							lineHeight: '130%',
							letterSpacing: '0.17px',
							color: 'var(--primary-dark)',
						}}
					>
						{dayjs(serviceDetail.serviceExecution?.endDateTime ?? serviceDetail.service?.endDateTime).format('HH:mm')}
					</Typography>
					<Typography
						variant='body2'
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 400,
							fontSize: '0.75rem',
							lineHeight: '130%',
							letterSpacing: '0.17px',
							color: 'var(--primary-dark)',
						}}
					>
						{dayjs(serviceDetail.serviceExecution?.endDateTime ?? serviceDetail.service?.endDateTime).format(
							'DD/MM/YYYY',
						)}
					</Typography>
				</Box>
			</Box>
		)
	}

	return (
		<Box
			sx={{
				width: '14.25rem',
				margin: 0,
				padding: '0.5rem 1rem',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				gap: 0.25,
			}}
		>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'auto 1fr',
					justifyContent: 'start',
					alignItems: 'baseline',
					gap: 0.25,
					fontFamily: 'var(--font-family)',
					fontWeight: 400,
					fontSize: '0.75rem',
					lineHeight: '130%',
					letterSpacing: '0.17px',
					color: 'var(--primary-dark)',
				}}
			>
				<LightTooltip
					title='Hora de Início diferente da hora escalada'
					open={startTimeTooltipOpen && startTimeMatches}
					placement='top'
				>
					<div
						onMouseEnter={() => {
							if (startTimeMatches) setStartTimeTooltipOpen(true)
						}}
						onMouseLeave={() => setStartTimeTooltipOpen(false)}
					>
						<CustomTimePicker
							aria-label='Service Start Time'
							disabled={isSecondaryDriverService}
							onChange={onStartTimeChange}
							value={dayjs(`${startDate}T${startTime}`)}
							isWarning={startTimeMatches}
						/>
					</div>
				</LightTooltip>
				<CustomDatePicker
					aria-label='Service Start Date'
					disabled={isSecondaryDriverService}
					value={dayjs(`${startDate}T${startTime}`)}
					onChange={(newValue: Dayjs | null) => {
						if (newValue?.isValid()) onStartDateChange(newValue.format('YYYY-MM-DD'))
					}}
				/>
			</Box>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'auto 1fr',
					justifyContent: 'start',
					alignItems: 'baseline',
					gap: 0.25,
					fontFamily: 'Poppins, sans-serif',
					fontWeight: 400,
					fontSize: '12px',
					lineHeight: '130%',
					letterSpacing: '0.17px',
					color: 'var(--primary-dark)',
				}}
			>
				<LightTooltip
					title={'Hora de fim > 01h00m'}
					open={Boolean(endTimeTooltipOpen && showEndTimeTooltip)}
					placement='top'
				>
					<div
						onMouseEnter={() => {
							if (showEndTimeTooltip) setEndTimeTooltipOpen(true)
						}}
						onMouseLeave={() => setEndTimeTooltipOpen(false)}
					>
						<CustomTimePicker
							aria-label='Service End Time'
							disabled={isSecondaryDriverService}
							value={dayjs(`${endDate}T${endTime}`)}
							onChange={onEndTimeChange}
							isWarning={showEndTimeTooltip}
						/>
					</div>
				</LightTooltip>
				<CustomDatePicker
					disabled={isSecondaryDriverService}
					aria-label='Service End Date'
					onChange={(newValue: Dayjs | null) => {
						if (newValue?.isValid()) onEndDateChange(newValue.format('YYYY-MM-DD'))
					}}
					value={dayjs(`${endDate}T${endTime}`)}
				/>
			</Box>
		</Box>
	)
}
